import {
  type APIComponentAttributes,
  type APIEntityType,
  type APIReferenceAttributes,
  RelativeDateValue,
} from '@ardoq/api-types';
import { parseDate, getRelativeDateRange } from '@ardoq/date-time';
import { dateRangeOperations } from '@ardoq/date-range';
import {
  _ComponentDefaultFilterKeys,
  getColorRangeFormatting,
  getListFieldFormatting,
  getRelativeDateValue,
  legacyAffectsTypeNames,
  resolveColorRangeColor,
  toNumericFieldValue,
} from '@ardoq/filter-helpers';
import { isNil } from 'lodash';
import { resolveListField } from '../filterUtils';

export type ColorFilterDescriptor = {
  version: number | undefined;
  color: string;
  value: unknown;
  type: string | undefined;
  comparator: string | undefined;
  name: string;
  affectsTypeName: string | undefined;
  affectsTypeNames: string[] | undefined;
};

export type ConditionalFormattingCacheWrite = {
  included: boolean;
  color: string;
  version: number | undefined;
};

export type ConditionalFormattingOutcome = {
  returnValue: boolean;
  cacheWrite: ConditionalFormattingCacheWrite | null;
};

type ResolveFieldSortedValues = (
  filterAttributeName: string,
  entityType: APIEntityType.COMPONENT | APIEntityType.REFERENCE,
  affectsTypeName: string | undefined,
  affectsTypeNames: string[] | undefined
) => number[] | null;

export type ComputeConditionalFormattingArgs = {
  entityType: APIEntityType.COMPONENT | APIEntityType.REFERENCE;
  attributes: APIComponentAttributes | APIReferenceAttributes;
  included: boolean;
  typeName: string;
  incomingRefCount: number;
  outgoingRefCount: number;
  colorFilter: ColorFilterDescriptor | null;
  resolveFieldSortedValues: ResolveFieldSortedValues;
};

export const resolveDateRangeRelativeDateColor = (
  attributes: APIComponentAttributes | APIReferenceAttributes,
  filterAttributeName: string,
  relativeDateValue: RelativeDateValue,
  color: string
): string => {
  const dateRangeFieldName =
    dateRangeOperations.extractDateRangeFieldName(filterAttributeName);
  const startFieldName =
    dateRangeOperations.toStartDateName(dateRangeFieldName);
  const endFieldName = dateRangeOperations.toEndDateName(dateRangeFieldName);

  const startFieldValue: string | null = attributes[startFieldName] ?? null;
  const endFieldValue: string | null = attributes[endFieldName] ?? null;

  if (startFieldValue === null && endFieldValue === null) {
    return '';
  }

  const relativeDateFilterRange = getRelativeDateRange(relativeDateValue);
  if (!relativeDateFilterRange) {
    return '';
  }

  const { start: resolvedStartValue, end: resolvedEndValue } =
    dateRangeOperations.replaceEmptyDates({
      start: startFieldValue,
      end: endFieldValue,
    });

  const fieldStartDate = resolvedStartValue
    ? parseDate(resolvedStartValue)
    : null;
  const fieldEndDate = resolvedEndValue ? parseDate(resolvedEndValue) : null;

  if (
    (fieldStartDate && isNaN(fieldStartDate.getTime())) ||
    (fieldEndDate && isNaN(fieldEndDate.getTime()))
  ) {
    return '';
  }

  const overlaps =
    fieldStartDate &&
    fieldEndDate &&
    fieldStartDate <= relativeDateFilterRange.end &&
    relativeDateFilterRange.start <= fieldEndDate;

  return overlaps ? color : '';
};

export const computeConditionalFormatting = ({
  entityType,
  attributes,
  included,
  typeName,
  incomingRefCount,
  outgoingRefCount,
  colorFilter,
  resolveFieldSortedValues,
}: ComputeConditionalFormattingArgs): ConditionalFormattingOutcome => {
  if (!colorFilter) {
    return { returnValue: included, cacheWrite: null };
  }

  const {
    version: filterVersion,
    color,
    value: filterValue,
    type: filterType,
    comparator,
    name: filterAttributeName,
    affectsTypeName,
    affectsTypeNames,
  } = colorFilter;
  const isFormattingFilter = !!color;

  const listFieldFormatting = getListFieldFormatting(
    isFormattingFilter,
    filterValue,
    filterType
  );
  if (listFieldFormatting) {
    const fieldValue: string | undefined = attributes[filterAttributeName];
    const { included: listIncluded, color: listColor } = resolveListField(
      fieldValue,
      listFieldFormatting
    );
    if (!listIncluded) {
      return { returnValue: false, cacheWrite: null };
    }
    return {
      returnValue: true,
      cacheWrite: { included, color: listColor, version: filterVersion },
    };
  }

  const colorRangeFormatting = getColorRangeFormatting(
    isFormattingFilter,
    filterValue,
    filterType
  );
  if (colorRangeFormatting) {
    if (
      isNil(colorRangeFormatting.min.value) ||
      isNil(colorRangeFormatting.max.value)
    ) {
      return {
        returnValue: included,
        cacheWrite: { included, color: '', version: filterVersion },
      };
    }
    const rawFieldValue =
      filterAttributeName === _ComponentDefaultFilterKeys.INCOMING_REF_COUNT
        ? incomingRefCount
        : filterAttributeName === _ComponentDefaultFilterKeys.OUTGOING_REF_COUNT
          ? outgoingRefCount
          : attributes[filterAttributeName];
    const fieldValue = toNumericFieldValue(rawFieldValue);
    if (fieldValue === undefined) {
      return { returnValue: false, cacheWrite: null };
    }

    const colorRangeTypeFilter = legacyAffectsTypeNames(
      affectsTypeName,
      affectsTypeNames
    );
    if (colorRangeTypeFilter && !colorRangeTypeFilter.includes(typeName)) {
      return { returnValue: false, cacheWrite: null };
    }

    let fieldSortedValues: number[] | null = null;
    if (
      colorRangeFormatting.mode === 'percent' ||
      colorRangeFormatting.mode === 'percentile'
    ) {
      fieldSortedValues = resolveFieldSortedValues(
        filterAttributeName,
        entityType,
        affectsTypeName,
        affectsTypeNames
      );
      if (!fieldSortedValues) {
        return { returnValue: false, cacheWrite: null };
      }
    }

    const rangeColor = resolveColorRangeColor(
      colorRangeFormatting,
      fieldValue,
      fieldSortedValues
    );
    return {
      returnValue: included,
      cacheWrite: { included, color: rangeColor ?? '', version: filterVersion },
    };
  }

  const relativeDateValue = getRelativeDateValue(
    isFormattingFilter,
    filterValue,
    filterType,
    comparator ?? ''
  );
  if (relativeDateValue) {
    const isDateRangeField =
      dateRangeOperations.fieldNameIsPartOfDateRangeField(filterAttributeName);

    if (isDateRangeField) {
      return {
        returnValue: included,
        cacheWrite: {
          included,
          color: resolveDateRangeRelativeDateColor(
            attributes,
            filterAttributeName,
            relativeDateValue,
            color
          ),
          version: filterVersion,
        },
      };
    }

    const fieldValue = attributes[filterAttributeName];

    if (fieldValue === null || fieldValue === undefined) {
      return {
        returnValue: included,
        cacheWrite: { included, color: '', version: filterVersion },
      };
    }

    const dateRange = getRelativeDateRange(relativeDateValue);
    if (!dateRange) {
      return {
        returnValue: included,
        cacheWrite: { included, color: '', version: filterVersion },
      };
    }

    const fieldDate = parseDate(fieldValue);
    if (isNaN(fieldDate.getTime())) {
      return {
        returnValue: included,
        cacheWrite: { included, color: '', version: filterVersion },
      };
    }

    const isInRange =
      fieldDate >= dateRange.start && fieldDate <= dateRange.end;

    return {
      returnValue: included,
      cacheWrite: {
        included,
        color: isInRange ? color : '',
        version: filterVersion,
      },
    };
  }

  return {
    returnValue: included,
    cacheWrite: { included, color, version: filterVersion },
  };
};
